import { ZodError } from 'zod/v4';
import type { ZodType } from 'zod/v4';

import type { ErrorSchemaRecord } from '../types/error-schema.js';
import type {
  AbstractRequestConfig,
  AbstractResponse,
  BuilderConfig,
} from '../types/index.js';
import { UnknownResponseError } from './unknown-response-error.js';

/**
 * Handles errors that occur during HTTP requests.
 *
 * This function implements the error handling strategy based on the builder configuration:
 * - Calls `onError` callback if provided (always called first)
 * - If `useDiscriminatorResponse` is `true` and the error has a response:
 *   - If `errorSchema` is provided and has a matching status code, parse with that schema and RETURN
 *   - If `errorSchema` is provided but no match, throw `UnknownResponseError`
 *   - If no `errorSchema`, attempts to parse using `responseSchema` (existing behavior)
 * - Calls `onZodError` callback if validation fails
 * - Throws the error if it cannot be handled as a discriminated response
 *
 * @param config - Builder configuration containing error handling callbacks
 * @param error - The error that occurred (can be any type)
 * @param responseSchema - Optional Zod schema to validate error responses when
 *   `useDiscriminatorResponse` is enabled and no errorSchema match
 * @param errorSchema - Optional record mapping status codes to Zod schemas for error responses
 * @returns The parsed error response data if `useDiscriminatorResponse` is enabled
 *   and parsing succeeds, otherwise throws the error
 * @throws The original error, UnknownResponseError, or a ZodError if validation fails
 *
 * @example
 * ```ts
 * // With errorSchema
 * const result = handleError(
 *   { useDiscriminatorResponse: true },
 *   { response: { data: { error: 'Not found' }, status: 404 } },
 *   undefined,
 *   { 404: z.object({ error: z.string() }) }
 * )
 * // Returns: { error: 'Not found' }
 * ```
 */
// MINT-OVERRIDE: accept `request` as 5th param to forward to onZodError(err, resp, origErr, request)
// MINT-OVERRIDE: accept `fullResponse` as 6th param so the discriminator-mode
// success-on-error path can wrap the parsed error in AbstractResponse shape.
export function handleError(
  config: BuilderConfig<boolean>,
  error: unknown,
  responseSchema?: ZodType,
  errorSchema?: ErrorSchemaRecord,
  request?: AbstractRequestConfig, // MINT-OVERRIDE
  fullResponse?: boolean, // MINT-OVERRIDE
) {
  if (config.onError) {
    config.onError(error);
  }
  if (!config.useDiscriminatorResponse) {
    if (config.onZodError && error instanceof ZodError) {
      // MINT-OVERRIDE: pass request as 4th arg for debug/logging callsites
      config.onZodError(error, undefined, undefined, request);
    }
    throw error;
  }

  // Check if error has a response
  if (
    typeof error !== 'object' ||
    !error ||
    !('response' in error) ||
    !error.response
  ) {
    throw error;
  }

  const response = error.response as AbstractResponse<unknown>;

  // MINT-OVERRIDE: helper to wrap parsed payloads in AbstractResponse shape when
  // the caller (e.g. createStream) opted in via fullResponse.
  const wrap = <T>(parsed: T) =>
    fullResponse
      ? ({
          data: parsed,
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
        } satisfies AbstractResponse<T>)
      : parsed;

  // Check errorSchema first (has precedence over responseSchema)
  if (errorSchema) {
    const schema = errorSchema[response.status];
    if (schema) {
      try {
        const parsed = schema.parse(response.data) as Record<string, unknown>;
        // Inject __status for runtime discrimination of error types
        return wrap({ ...parsed, __status: response.status });
      } catch (e) {
        if (config.onZodError) {
          // MINT-OVERRIDE: pass request as 4th arg for debug/logging callsites
          config.onZodError(e as ZodError, response, error, request);
        }
        throw e;
      }
    }
    // No matching status code in errorSchema - throw UnknownResponseError
    throw new UnknownResponseError(response, response.status);
  }

  // Fall back to responseSchema for discriminated unions (existing behavior)
  if (responseSchema) {
    try {
      return wrap(responseSchema.parse(response.data));
    } catch (e) {
      if (config.onZodError) {
        // MINT-OVERRIDE: pass request as 4th arg for debug/logging callsites
        config.onZodError(e as ZodError, response, error, request);
      }
      throw e;
    }
  }

  throw error;
}

/**
 * @deprecated Use handleError instead. Will be removed in next major version.
 */
export const handleException = handleError;
